import { parse, subMonths } from 'date-fns';
import { getLatePayments, getOnTimePayments, isCollection, isOpenAccount } from './credit-report-utils';
import type { CreditReportType, ExperianDescription, LocalDate, TradeLine } from '@zg-rentals/ts-capps-client';
import { DATA_UNAVAILABLE_TEXT } from '../../constants/credit-report-constants';
import { getCurrencyDisplayFromDollars, getTitleCase, parseDateObject } from '@zg-rentals/formatters';
import type {
  PaymentAccount,
  PaymentHistories,
  PaymentHistoryItemCommon,
  PaymentHistoryShowConfig,
} from '../../models';

export const getCreditAccounts = (creditReport: CreditReportType) => {
  const { fullCreditProfile } = creditReport;
  const openAccounts: Array<PaymentAccount> = [];
  const closedAccounts: Array<PaymentAccount> = [];
  if (fullCreditProfile && fullCreditProfile.TradeLine) {
    const { TradeLine } = fullCreditProfile;
    const allAccounts = TradeLine.filter((account) => !isCollection(account));
    allAccounts.forEach((account) => {
      const {
        OpenDate,
        SubscriberDisplayName,
        BalanceAmount,
        AccountType,
        BalanceDate,
        PaymentProfile,
        PaymentGridJ9,
        ConsumerComment,
        SpecialComment,
        FirstDelinquencyDate,
      } = account;
      const onTimePayments = PaymentProfile ? getOnTimePayments(PaymentProfile) : DATA_UNAVAILABLE_TEXT;
      const latePayments = getLatePayments(account);
      const lastLatePayment = FirstDelinquencyDate;
      const accountTypeAndName = getAccountTypeAndName(AccountType?.desc, SubscriberDisplayName);
      const hasAccountName = AccountType?.desc || SubscriberDisplayName;
      const isOpen = isOpenAccount(account);
      const paymentHistory: PaymentHistories = {
        j9: PaymentGridJ9
          ? {
              items: getPaymentGridJ9History(PaymentGridJ9),
              type: 'j9',
            }
          : null,
        legacy: PaymentProfile
          ? {
              items: getPaymentHistory(BalanceDate, PaymentProfile),
              type: 'legacy',
            }
          : null,
      };

      if (BalanceAmount || hasAccountName) {
        const newAccount: PaymentAccount = {
          name: accountTypeAndName,
          amount: BalanceAmount ? getCurrencyDisplayFromDollars({ amount: BalanceAmount, hideCents: true }) : '$0',
          openDate: OpenDate || DATA_UNAVAILABLE_TEXT,
          onTimePayments: onTimePayments != null ? `${onTimePayments}` : DATA_UNAVAILABLE_TEXT,
          latePayments: latePayments != null ? `${latePayments}` : DATA_UNAVAILABLE_TEXT,
          lastLatePayment: lastLatePayment != null ? lastLatePayment : DATA_UNAVAILABLE_TEXT,
          accountComment: getAccountComment(ConsumerComment, SpecialComment),
          paymentHistory,
        };
        if (isOpen) {
          openAccounts.push(newAccount);
        } else {
          closedAccounts.push(newAccount);
        }
      }
    });
  }
  return { openAccounts, closedAccounts };
};

export const getAccountTypeAndName = (description?: string, name?: string) => {
  if (!description && !name) {
    return 'name unavailable';
  }
  if (!name) {
    return description || '';
  }
  return `${getTitleCase(name)}${description ? ` ${description}` : ''}`;
};

const getAccountComment = (consumerComment?: string, specialComment?: ExperianDescription) => {
  if (consumerComment) {
    return consumerComment;
  }
  if (specialComment?.desc) {
    return specialComment.desc;
  }
  return null;
};

export const getPaymentHistory = (
  balanceDateString?: string | LocalDate,
  paymentProfileString?: string,
): Array<PaymentHistoryItemCommon> => {
  if (!balanceDateString || !paymentProfileString) {
    return [];
  }
  const balanceDate =
    typeof balanceDateString === 'string'
      ? parse(balanceDateString, 'MM/dd/yyyy', new Date())
      : parseDateObject(balanceDateString);

  return Array.from(paymentProfileString).map((code, i) => {
    const date = subMonths(balanceDate, i);
    return { year: date.getFullYear(), month: date.getMonth() + 1, code };
  });
};

const PAST_DUE_DAYS_BY_CODE: Record<string, number> = {
  A11: 30,
  A12: 60,
  A13: 90,
  A14: 120,
  A15: 150,
  A16: 180,
};

export const getPastDueDays = (code?: string): number | undefined =>
  code ? PAST_DUE_DAYS_BY_CODE[code.trim()] : undefined;

export const getPaymentGridJ9History = (
  paymentGridJ9?: TradeLine['PaymentGridJ9'],
): Array<PaymentHistoryItemCommon> => {
  return Object.entries(paymentGridJ9 ?? {})
    .filter(([yearKey]) => yearKey.length === 4 && Number.isInteger(Number(yearKey)))
    .flatMap(([yearKey, monthMap]) =>
      Object.entries(monthMap ?? {})
        .filter(([monthKey, entry]) => {
          const month = Number(monthKey);
          return Boolean(entry) && Number.isInteger(month) && month >= 1 && month <= 12;
        })
        .map(([monthKey, entry]) => ({
          year: Number(yearKey),
          month: Number(monthKey),
          code: entry.code,
          desc: entry.desc,
        })),
    );
};

export const pickPaymentHistory = (paymentHistory: PaymentHistories, config: PaymentHistoryShowConfig) => {
  const picked =
    [config.j9 ? paymentHistory.j9 : null, config.legacy ? paymentHistory.legacy : null].filter(Boolean)[0] ?? null;
  // Do not render if we have no items:
  return picked && picked.items.length > 0 ? picked : null;
};
